/** Modèles CDC — Maîtrise des risques (Planification). */

export const MAX_RISKS_PER_CATEGORY = 3;

export const RISK_CATEGORY_KEYS = [
  'strategiques_marche',
  'financiers',
  'operationnels',
  'organisationnels_humains',
  'technologiques_reglementaires',
  'contexte',
] as const;

export type RiskCategoryKey = (typeof RISK_CATEGORY_KEYS)[number];

export const RISK_CATEGORY_LABELS: Record<RiskCategoryKey, string> = {
  strategiques_marche: 'Risques stratégiques et de marché',
  financiers: 'Risques financiers',
  operationnels: 'Risques opérationnels',
  organisationnels_humains: 'Risques organisationnels et humains',
  technologiques_reglementaires: 'Risques technologiques et réglementaires',
  contexte: 'Risques du contexte',
};

export type RiskCriticite = 'Critique' | 'Élevé' | 'Modéré' | 'Faible';

export const RISK_CRITICITE_COLORS: Record<RiskCriticite, string> = {
  Critique: '#c62828',
  Élevé: '#ef6c00',
  Modéré: '#f9a825',
  Faible: '#2e7d32',
};

export interface RiskItem {
  id: string;
  titre: string;
  description: string;
  category: RiskCategoryKey;
  /** null = non encore tranché ; true = pertinent ; false = non pertinent */
  pertinent: boolean | null;
}

export interface RiskCriticalityItem extends RiskItem {
  causes: string;
  consequences: string;
  P: number;
  I: number;
  C: number;
  criticite: RiskCriticite;
}

export interface RiskMeasureItem extends RiskCriticalityItem {
  /** Plan de maîtrise proposé / amendé */
  plan_maitrise: string;
  mesures_preventives?: string;
  mesures_contingence?: string;
}

export type RisksMap = Record<RiskCategoryKey, RiskItem[]>;
export type CriticalityMap = Record<RiskCategoryKey, RiskCriticalityItem[]>;
export type MeasuresMap = Record<RiskCategoryKey, RiskMeasureItem[]>;

export function emptyRisksMap(): RisksMap {
  return {
    strategiques_marche: [],
    financiers: [],
    operationnels: [],
    organisationnels_humains: [],
    technologiques_reglementaires: [],
    contexte: [],
  };
}

/** Mappe d’anciennes clés vers le modèle CDC. */
export function migrateRisksMap(raw: Record<string, unknown> | null | undefined): RisksMap {
  const map = emptyRisksMap();
  if (!raw || typeof raw !== 'object') return map;

  const aliases: Record<string, RiskCategoryKey> = {
    strategiques_marche: 'strategiques_marche',
    financiers: 'financiers',
    operationnels: 'operationnels',
    organisationnels_humains: 'organisationnels_humains',
    technologiques_reglementaires: 'technologiques_reglementaires',
    contexte: 'contexte',
    techniques: 'technologiques_reglementaires',
    juridiques: 'technologiques_reglementaires',
    commerciaux: 'contexte',
  };

  let idx = 1;
  for (const [oldKey, list] of Object.entries(raw)) {
    const cat = aliases[oldKey];
    if (!cat || !Array.isArray(list)) continue;
    for (const item of list as Partial<RiskItem>[]) {
      if ((map[cat].length ?? 0) >= MAX_RISKS_PER_CATEGORY) break;
      map[cat].push({
        id: item.id || `R${String(idx).padStart(2, '0')}`,
        titre: item.titre ?? '',
        description: item.description ?? '',
        category: cat,
        pertinent: typeof item.pertinent === 'boolean' ? item.pertinent : null,
      });
      idx++;
    }
  }
  return map;
}

export function criticiteFromC(c: number): RiskCriticite {
  if (c >= 20) return 'Critique';
  if (c >= 12) return 'Élevé';
  if (c >= 6) return 'Modéré';
  return 'Faible';
}

export function criticiteCssClass(c: RiskCriticite): string {
  switch (c) {
    case 'Critique':
      return 'risk-crit-critique';
    case 'Élevé':
      return 'risk-crit-eleve';
    case 'Modéré':
      return 'risk-crit-modere';
    default:
      return 'risk-crit-faible';
  }
}

export function withComputedC(
  item: Omit<RiskCriticalityItem, 'C' | 'criticite'> & { C?: number; criticite?: string },
): RiskCriticalityItem {
  const C = Math.min(25, Math.max(1, (item.P || 1) * (item.I || 1)));
  return {
    ...item,
    causes: item.causes ?? '',
    consequences: item.consequences ?? '',
    pertinent: item.pertinent ?? true,
    C,
    criticite: criticiteFromC(C),
  };
}

export function onlyPertinent(map: RisksMap): RisksMap {
  const out = emptyRisksMap();
  for (const key of RISK_CATEGORY_KEYS) {
    out[key] = (map[key] ?? []).filter((r) => r.pertinent === true).slice(0, MAX_RISKS_PER_CATEGORY);
  }
  return out;
}

export function flattenRisks(map: RisksMap): RiskItem[] {
  return RISK_CATEGORY_KEYS.flatMap((k) => map[k] ?? []);
}

export function newRiskId(index: number): string {
  return `R${String(index).padStart(2, '0')}`;
}

export function countInCategory(map: RisksMap, category: RiskCategoryKey): number {
  return (map[category] ?? []).length;
}
